// frontend/src/context/EventContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axiosClient from '../api/axiosClient';
import { useAuth } from './AuthContext';

const EventContext = createContext();

// El provider acepta un prop 'initialEventDetails' y 'eventPublished'
export const EventProvider = ({ children, initialEventDetails = {}, eventPublished = false }) => {
  const { token } = useAuth();

  // Estado para detalles de un evento individual
  const [eventDetails, setEventDetails] = useState(initialEventDetails);
  // Estado para saber si el evento fue publicado recientemente
  const [isEventPublished, setIsEventPublished] = useState(eventPublished);
  
  // Estado para la vista actual (navegación principal)
  const [currentView, setCurrentView] = useState('upcoming');
  
  // Estado para mis eventos (organizados e inscritos) - estructura jerárquica
  const [myEvents, setMyEvents] = useState({
    organized: { upcoming: [], past: [] },
    enrolled: { upcoming: [], past: [] },
  });

  // Estado para eventos de la vista actual
  const [currentViewEvents, setCurrentViewEvents] = useState({
    organized: [],
    enrolled: []
  });

  // Estados de filtros independientes para cada vista
  const [viewFilters, setViewFilters] = useState({
    upcoming: {
      enrollmentStatus: [],
      eventType: [],
      vehicle: '',
      terrain: [],
      price: [],
      difficulty: [],
      showOrganizedEvents: true
    },
    past: {
      enrollmentStatus: [],
      eventType: [],
      vehicle: '',
      terrain: [],
      price: [],
      difficulty: [],
      showOrganizedEvents: true
    }
  });

  // Estado para el loading
  const [loading, setLoading] = useState(true);

  // Función para actualizar los eventos con los datos de la API usando filtros jerárquicos
  const fetchMyEvents = async (view = currentView, filters = {}) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        view,
        ...filters
      });

      const response = await axiosClient.get(`/api/events/hosted-events?${params}`);
      if (response.data && response.data.success) {
        // Actualizar eventos de la vista actual
        setCurrentViewEvents({
          organized: response.data.events.organized,
          enrolled: response.data.events.enrolled
        });

        // Mantener estructura legacy para compatibilidad
        setMyEvents({
          organized: response.data.organized,
          enrolled: response.data.enrolled,
        });
      } else {
        console.error('Error al obtener los eventos:', response.data.message);
      }
    } catch (error) {
      console.error('Error fetching my events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función para cambiar la vista (navegación principal)
  const changeView = useCallback(async (newView) => {
    if (newView !== currentView) {
      setCurrentView(newView);
      
      // Obtener filtros para la nueva vista
      const filtersForView = viewFilters[newView];
      
      // Cargar eventos para la nueva vista con sus filtros
      await fetchMyEvents(newView, filtersForView);
    }
  }, [currentView, viewFilters]);

  // Función para aplicar filtros secundarios en la vista actual
  const applyFilters = useCallback(async (filters) => {
    // Actualizar filtros para la vista actual
    setViewFilters(prev => ({
      ...prev,
      [currentView]: filters
    }));
    
    // Recargar eventos con los nuevos filtros
    await fetchMyEvents(currentView, filters);
  }, [currentView]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    fetchMyEvents();
  }, [token]);

  // Actualizar el estado de publicación cuando cambie el prop
  useEffect(() => {
    setIsEventPublished(eventPublished);
  }, [eventPublished]);

  // Actualizar eventDetails cuando cambien los initialEventDetails
  useEffect(() => {
    if (initialEventDetails && Object.keys(initialEventDetails).length > 0) {
      setEventDetails(initialEventDetails);
    }
  }, [initialEventDetails]);

  const updateEventDetails = useCallback((newDetails) => {
    // Si los nuevos detalles incluyen tickets, recalcular la capacidad total
    if (newDetails.tickets && Array.isArray(newDetails.tickets)) {
      const totalCapacity = newDetails.tickets.reduce((sum, ticket) => sum + (ticket.capacity || 0), 0);
      const totalAvailableSeats = newDetails.tickets.reduce((sum, ticket) => sum + (ticket.availableSeats || 0), 0);
      
      setEventDetails({
        ...newDetails,
        capacity: totalCapacity,
        availableSeats: totalAvailableSeats
      });
    } else {
      setEventDetails(newDetails);
    }
  }, []);

  // Función para actualizar la capacidad del evento basada en los tickets
  const updateEventCapacity = useCallback((tickets) => {
    if (tickets && Array.isArray(tickets)) {
      const totalCapacity = tickets.reduce((sum, ticket) => sum + (ticket.capacity || 0), 0);
      const totalAvailableSeats = tickets.reduce((sum, ticket) => sum + (ticket.availableSeats || 0), 0);
      
      setEventDetails(prevDetails => ({
        ...prevDetails,
        capacity: totalCapacity,
        availableSeats: totalAvailableSeats,
        tickets: tickets
      }));
    }
  }, []);

  return (
    <EventContext.Provider value={{ 
      eventDetails, 
      updateEventDetails, 
      updateEventCapacity,
      myEvents, 
      currentViewEvents,
      currentView,
      viewFilters,
      changeView,
      applyFilters,
      loading, 
      fetchMyEvents, 
      isEventPublished 
    }}>
      {children}
    </EventContext.Provider>
  );
};

export const useEventContext = () => useContext(EventContext);
